import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { socket, socketGet } from "../../services/socket-client";
import { appendDelta, receiveCompleted, receiveFailed, setActiveTool } from "./assistant.slice";
import { AssistantMessage } from "../../types/Assistant";

interface DeltaEvent {
  conversationId: string;
  seq: number;
  text: string;
}
interface ToolEvent {
  conversationId: string;
  tool: string;
}
interface CompletedEvent {
  conversationId: string;
  message: AssistantMessage;
}
interface FailedEvent {
  conversationId: string;
  errorCode: string;
}

/**
 * Subscribes to the assistant conversation room and streams the answer in.
 *
 * Progressive rendering only — the REST response carries the authoritative
 * message, so losing the socket costs live typing, never the answer itself.
 */
const subscribed = new Set<string>();

/**
 * Join the conversation room. Awaited by the sender before the first prompt, so
 * the very first answer streams too — the effect below alone would subscribe
 * only after the REST call had already returned the finished reply.
 *
 * Streaming is best-effort: a failure here costs live typing, not the answer.
 */
export async function subscribeToAssistant(companyId: string, conversationId: string) {
  const key = `${companyId}:${conversationId}`;
  if (subscribed.has(key)) return;
  try {
    await socketGet(`/${companyId}/sockets/subscribe-assistant/${conversationId}`);
    subscribed.add(key);
  } catch {
    /* keep going without live deltas */
  }
}

export function useAssistantSocket(companyId?: string, conversationId?: string | null) {
  const dispatch = useDispatch();

  useEffect(() => {
    if (!companyId || !conversationId) return;
    void subscribeToAssistant(companyId, conversationId);
  }, [companyId, conversationId]);

  useEffect(() => {
    const onDelta = (payload: unknown) => {
      const event = payload as DeltaEvent;
      if (!conversationId || event.conversationId !== conversationId) return;
      dispatch(appendDelta({ text: event.text }));
    };
    const onTool = (payload: unknown) => {
      const event = payload as ToolEvent;
      if (!conversationId || event.conversationId !== conversationId) return;
      dispatch(setActiveTool(event.tool));
    };
    const onCompleted = (payload: unknown) => {
      const event = payload as CompletedEvent;
      if (!conversationId || event.conversationId !== conversationId) return;
      dispatch(receiveCompleted(event.message));
    };
    const onFailed = (payload: unknown) => {
      const event = payload as FailedEvent;
      if (!conversationId || event.conversationId !== conversationId) return;
      dispatch(receiveFailed({ errorCode: event.errorCode }));
    };

    socket.on("assistant-delta", onDelta);
    socket.on("assistant-tool", onTool);
    socket.on("assistant-completed", onCompleted);
    socket.on("assistant-failed", onFailed);

    return () => {
      socket.off("assistant-delta", onDelta);
      socket.off("assistant-tool", onTool);
      socket.off("assistant-completed", onCompleted);
      socket.off("assistant-failed", onFailed);
    };
  }, [dispatch, conversationId]);
}
